import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
} from '@mui/material';
import { Refresh as RefreshIcon, Download as DownloadIcon } from '@mui/icons-material';
import { reportService, ActiveTimerReportEntry, TimeTrackerReportFilters } from '../services/reportService';
import { exportToCSV } from '../utils/csvExport';
import { useAuth } from '../contexts/AuthContext';
import { formatDate } from '../utils/date';

const ActiveTimerReport: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  // Check if user is authorized
  const isAuthorized = () => {
    if (!user) return false;
    // PMP Admin, Site Admin, or Client Manager can always access
    if (user.roles && user.roles.some(role => 
      ['pmp_admin', 'site_admin', 'internal_manager'].includes(role.slug)
    )) {
      return true;
    }
    // Other users can access if they have a firm association (backend will filter appropriately)
    return !!(user.firm_id || user.firm);
  };

  useEffect(() => {
    if (user && !isAuthorized()) {
      navigate('/', { replace: true });
    }
  }, [user, navigate]);

  // Filter state
  const [clientFirmId, setClientFirmId] = useState<number | ''>('');
  const [userId, setUserId] = useState<number | ''>('');

  // Filter options
  const [filters, setFilters] = useState<TimeTrackerReportFilters>({
    client_firms: [],
    users: [],
    activities: [],
    dispositions: [],
  });

  // Data state
  const [reportData, setReportData] = useState<ActiveTimerReportEntry[]>([]);
  const [originalReportData, setOriginalReportData] = useState<ActiveTimerReportEntry[]>([]); // Store original data from backend
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
  const [dataLoadTime, setDataLoadTime] = useState<number>(Date.now());

  // Load filter options on mount
  useEffect(() => {
    if (isAuthorized()) {
      loadFilters();
      loadReport();
    }
  }, [user]);

  // Auto-refresh every 60 seconds
  useEffect(() => {
    if (!isAuthorized()) return;

    const interval = setInterval(() => {
      loadReport();
    }, 60000); // Refresh every 60 seconds

    return () => clearInterval(interval);
  }, [clientFirmId, userId, isAuthorized()]);

  // Update elapsed times every second
  useEffect(() => {
    if (originalReportData.length === 0) return;

    const interval = setInterval(() => {
      const now = Date.now();
      const timeSinceLoad = Math.floor((now - dataLoadTime) / 1000);
      
      setReportData(originalReportData.map(entry => {
        // Use the original elapsed_seconds from backend and add the time since data was loaded
        // This avoids timezone issues when parsing date strings
        const elapsedSeconds = entry.elapsed_seconds + timeSinceLoad;
        
        const elapsedHours = Math.floor(elapsedSeconds / 3600);
        const elapsedMinutes = Math.floor((elapsedSeconds % 3600) / 60);
        const elapsedSecs = elapsedSeconds % 60;
        const elapsedFormatted = `${String(elapsedHours).padStart(2, '0')}:${String(elapsedMinutes).padStart(2, '0')}:${String(elapsedSecs).padStart(2, '0')}`;

        return {
          ...entry,
          elapsed_seconds: elapsedSeconds,
          elapsed_formatted: elapsedFormatted,
          elapsed_hours: Math.round((elapsedSeconds / 3600) * 100) / 100,
        };
      }));
    }, 1000); // Update every second

    return () => clearInterval(interval);
  }, [originalReportData, dataLoadTime]);

  const loadFilters = async () => {
    try {
      const filterData = await reportService.getTimeTrackerReportFilters();
      setFilters(filterData);
    } catch (err: any) {
      console.error('Failed to load filters:', err);
      setError('Failed to load filter options');
    }
  };

  const loadReport = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params: any = {};
      
      if (clientFirmId) {
        params.client_firm_id = clientFirmId;
      }
      if (userId) {
        params.user_id = userId;
      }

      const data = await reportService.getActiveTimerReport(params);
      const reportDataArray = data as ActiveTimerReportEntry[];
      setOriginalReportData(reportDataArray); // Store original data from backend
      setReportData(reportDataArray); // Also set to display data
      setLastRefresh(new Date());
      setDataLoadTime(Date.now()); // Store when we loaded the data for accurate elapsed time calculation
    } catch (err: any) {
      console.error('Error fetching active timer report:', err);
      setError(err.response?.data?.message || 'Failed to fetch active timer report');
    } finally {
      setLoading(false);
    }
  }, [clientFirmId, userId]);

  const handleRefresh = () => {
    loadReport();
  };

  const handleExportCSV = () => {
    exportToCSV<ActiveTimerReportEntry>(
      reportData,
      [
        { key: 'client', label: 'Client' },
        { key: 'case_id', label: 'Case ID' },
        { key: 'case_name', label: 'Case Name' },
        { key: 'file_number', label: 'File Number' },
        { key: 'user', label: 'User' },
        { key: 'started_at', label: 'Started At' },
        { key: 'elapsed_formatted', label: 'Elapsed Time' },
        { key: 'elapsed_hours', label: 'Elapsed Hours' },
      ],
      `active-timer-report_${new Date().toISOString().slice(0, 10)}.csv`
    );
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  if (!isAuthorized()) {
    return (
      <Box>
        <Alert severity="error">
          You do not have permission to access this page.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">
          Active Timer Report
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Last refreshed: {lastRefresh.toLocaleTimeString()}
          </Typography>
          <IconButton onClick={handleRefresh} disabled={loading} color="primary">
            <RefreshIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Filter
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
          <Box sx={{ flex: 1, minWidth: { xs: '100%', md: '200px' } }}>
            <FormControl fullWidth>
              <InputLabel>Client List</InputLabel>
              <Select
                value={clientFirmId}
                label="Client List"
                onChange={(e) => {
                  setClientFirmId(e.target.value as number | '');
                  // Auto-refresh when filter changes
                  setTimeout(() => {
                    loadReport();
                  }, 100);
                }}
              >
                <MenuItem value="">All</MenuItem>
                {filters.client_firms.map((firm) => (
                  <MenuItem key={firm.id} value={firm.id}>
                    {firm.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ flex: 1, minWidth: { xs: '100%', md: '200px' } }}>
            <FormControl fullWidth>
              <InputLabel>User List</InputLabel>
              <Select
                value={userId}
                label="User List"
                onChange={(e) => {
                  setUserId(e.target.value as number | '');
                  // Auto-refresh when filter changes
                  setTimeout(() => {
                    loadReport();
                  }, 100);
                }}
              >
                <MenuItem value="">All</MenuItem>
                {filters.users.map((user) => (
                  <MenuItem key={user.id} value={user.id}>
                    {user.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ minWidth: { xs: '100%', md: '120px' } }}>
            <Button
              fullWidth
              variant="outlined"
              color="primary"
              onClick={handleExportCSV}
              disabled={reportData.length === 0}
              startIcon={<DownloadIcon />}
              sx={{ height: '56px' }}
            >
              Export CSV
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Results */}
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            Active Timers ({reportData.length})
          </Typography>
        </Box>

        {loading && reportData.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : reportData.length === 0 ? (
          <Alert severity="info">
            No active timers found.
          </Alert>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Client</strong></TableCell>
                  <TableCell><strong>Case ID</strong></TableCell>
                  <TableCell><strong>Case Name</strong></TableCell>
                  <TableCell><strong>File Number</strong></TableCell>
                  <TableCell><strong>User</strong></TableCell>
                  <TableCell><strong>Started At</strong></TableCell>
                  <TableCell><strong>Elapsed Time</strong></TableCell>
                  <TableCell><strong>Elapsed Hours</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reportData.map((entry) => (
                  <TableRow key={entry.session_id} hover>
                    <TableCell>{entry.client}</TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        onClick={() => {
                          if (entry.client_firm_id) {
                            navigate(`/firms/${entry.client_firm_id}/cases/${entry.case_id}`);
                          } else {
                            navigate(`/cases/${entry.case_id}`);
                          }
                        }}
                      >
                        {entry.case_id}
                      </Button>
                    </TableCell>
                    <TableCell>{entry.case_name}</TableCell>
                    <TableCell>{entry.file_number || '-'}</TableCell>
                    <TableCell>{entry.user}</TableCell>
                    <TableCell>
                      {formatDate(entry.started_at, {
                        weekday: 'long',
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit',
                        hour12: true,
                      })}
                    </TableCell>
                    <TableCell>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontFamily: 'monospace',
                          fontWeight: 'bold',
                          color: 'primary.main'
                        }}
                      >
                        {entry.elapsed_formatted}
                      </Typography>
                    </TableCell>
                    <TableCell>{entry.elapsed_hours.toFixed(2)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
};

export default ActiveTimerReport;
